import * as React from 'react'
import { tw } from 'utils/classnames'
import type {
  RenderPhotoProps,
  PhotoAlbumProps,
  RenderRowContainerProps,
  Photo,
} from 'react-photo-album'
import { PhotoAlbum as PhotoAlbumLib } from 'react-photo-album'
import Image from 'next/image'

import { HoverActions } from '@web/components/hover-actions/hover-actions'

export interface PhotoAlbumPhoto extends Photo {
  href: string
  desktopSrc?: string
  desktopWidth?: number
  id: string
  queryId?: string
  position?: number
}

function LinkedPhoto({
  imageProps: { src, alt, title, sizes, className, onClick },
  wrapperStyle,
  photo,
  target,
  layout,
}: RenderPhotoProps<PhotoAlbumPhoto> & { target?: React.HTMLAttributeAnchorTarget }) {
  const style = {
    '--photo-width': `${photo.width}px`,
  } as React.CSSProperties

  const sizeWidth = photo.width < layout.width ? layout.width : photo.width

  const imageSrc =
    photo.desktopSrc && (photo.height < layout.height || photo.width < layout.width)
      ? photo.desktopSrc
      : src

  return (
    //TODO - move back to Link component once the product page is migrated to cf-web
    //We need to use the standard anchor tag for now as the Link component
    //is doing a prefetch on hover which is slow and seems unresponsive
    <div className="relative" style={{ ...wrapperStyle, ...style }} data-testid="photo-album-photo">
      <a target={target} href={photo.href} className="static">
        <Image
          fill
          src={imageSrc as string}
          alt={alt}
          title={title}
          sizes={sizes ? sizes : `(max-width: 640px) 100vw, ${sizeWidth}px`}
          className={tw('relative h-full w-full cursor-pointer', className)}
          onClick={onClick}
          unoptimized
        />
      </a>
      <HoverActions id={photo.id} imageSrc={photo.src} href={photo.href} title={photo.title} />
    </div>
  )
}

function PhotoAlbumImage(props: RenderPhotoProps<PhotoAlbumPhoto>) {
  return <LinkedPhoto {...props} />
}

function PhotoAlbumImageNewTab(props: RenderPhotoProps<PhotoAlbumPhoto>) {
  return <LinkedPhoto {...props} target="_blank" />
}

function RenderRowContainer({
  rowsCount,
  rowContainerProps,
  children,
}: RenderRowContainerProps<PhotoAlbumPhoto>) {
  return (
    <div
      className={tw(rowsCount === 1 && 'photoAlbum singleRow', rowContainerProps.className)}
      style={{
        ...rowContainerProps.style,
        flexDirection: undefined,
        justifyContent: rowsCount === 1 ? 'flex-start' : 'space-between',
        gap: rowsCount === 1 ? '2px' : '',
      }}
    >
      {children}
    </div>
  )
}

export function PhotoAlbum(
  props: PhotoAlbumProps<PhotoAlbumPhoto> & { openPhotoInNewTab?: boolean },
) {
  const { photos, layout, openPhotoInNewTab, ...otherProps } = props
  const renderPhotoComponent = openPhotoInNewTab ? PhotoAlbumImageNewTab : PhotoAlbumImage

  return (
    <PhotoAlbumLib
      layout={layout}
      photos={photos}
      renderRowContainer={RenderRowContainer}
      renderPhoto={renderPhotoComponent}
      defaultContainerWidth={1200}
      {...otherProps}
    />
  )
}
